import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Plus, Building2, PauseCircle, PlayCircle, XCircle, LogIn, AlertTriangle, BellOff, SlidersHorizontal } from "lucide-react";
import { api, startImpersonation } from "@/lib/api";
import { CompanyFeaturesDialog, FEATURE_KEYS, resolveFeatures } from "@/components/admin/CompanyFeaturesDialog";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";
import { formatDate, STATUS_LABEL } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const STATUS_COLORS = {
  trial: "bg-amber-100 text-amber-800 border-amber-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  suspended: "bg-stone-200 text-stone-700 border-stone-300",
  cancelled: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function AdminCompanies() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [featuresFor, setFeaturesFor] = useState(null);
  const navigate = useNavigate();
  const planById = (id) => plans.find((p) => p.id === id);
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    legal_name: "",
    trade_name: "",
    cnpj: "",
    responsible_name: "",
    email: "",
    phone: "",
    plan_id: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/companies");
      setItems(data);
    } catch (err) {
      console.error("Failed to load companies", err);
    }
  };

  useEffect(() => {
    load();
    api
      .get("/admin/plans")
      .then(({ data }) => setPlans(data))
      .catch((err) => console.error("Failed to load plans for company form", err));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/companies", form);
      setOpen(false);
      setForm({ legal_name: "", trade_name: "", cnpj: "", responsible_name: "", email: "", phone: "", plan_id: "" });
      toast.success("Empresa criada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar");
    }
  };

  const act = async (id, action) => {
    try {
      if (action === "delete") {
        await api.delete(`/admin/companies/${id}`);
      } else {
        await api.post(`/admin/companies/${id}/${action}`);
      }
      toast.success("Ação realizada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const impersonate = async (company) => {
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/impersonate`);
      startImpersonation({ token: data.token, company: data.company });
      await refresh();
      toast.success(`Modo suporte ativado em ${company.trade_name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao acessar painel");
    }
  };

  return (
    <Layout
      title="Empresas"
      subtitle="Gestão completa de tenants da plataforma"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid={TID.adminCompaniesNew}>
              <Plus className="h-4 w-4 mr-2" /> Nova empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">Nova empresa</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Razão Social *</Label>
                  <Input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} required className="mt-1 bg-white" />
                </div>
                <div>
                  <Label>Nome Fantasia *</Label>
                  <Input value={form.trade_name} onChange={(e) => set("trade_name", e.target.value)} required className="mt-1 bg-white" />
                </div>
                <div>
                  <Label>CNPJ *</Label>
                  <Input value={form.cnpj} onChange={(e) => set("cnpj", e.target.value)} required className="mt-1 bg-white" />
                </div>
                <div>
                  <Label>Responsável *</Label>
                  <Input value={form.responsible_name} onChange={(e) => set("responsible_name", e.target.value)} required className="mt-1 bg-white" />
                </div>
                <div>
                  <Label>E-mail *</Label>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-1 bg-white" />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 bg-white" />
                </div>
                <div>
                  <Label>Plano</Label>
                  <select
                    value={form.plan_id}
                    onChange={(e) => set("plan_id", e.target.value)}
                    className="mt-1 w-full h-10 rounded-md border border-stone-300 bg-white px-3 text-sm font-body"
                  >
                    <option value="">— Selecione —</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 text-white">Criar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <Card data-testid={TID.adminCompanies} className="rounded-xl border-stone-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Empresa</TableHead>
              <TableHead className="font-body">CNPJ</TableHead>
              <TableHead className="font-body">Contato</TableHead>
              <TableHead className="font-body">Plano</TableHead>
              <TableHead className="font-body">Recursos ativos</TableHead>
              <TableHead className="font-body">Status</TableHead>
              <TableHead className="font-body">Cadastro</TableHead>
              <TableHead className="font-body text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-stone-500 py-10">
                  <Building2 className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  Sem empresas cadastradas
                </TableCell>
              </TableRow>
            )}
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`${TID.companyRow}-${c.id}`}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => impersonate(c)}
                    className="text-left group focus:outline-none"
                    data-testid={`company-open-${c.id}`}
                    title="Abrir painel da empresa (modo suporte)"
                  >
                    <div className="font-display font-semibold text-emerald-800 group-hover:underline">
                      {c.trade_name}
                    </div>
                    <div className="text-xs text-stone-500 font-body">{c.legal_name}</div>
                  </button>
                </TableCell>
                <TableCell className="font-body text-sm">{c.cnpj}</TableCell>
                <TableCell className="font-body text-sm">
                  <div>{c.responsible_name}</div>
                  <div className="text-stone-500 text-xs">{c.email}</div>
                </TableCell>
                <TableCell className="font-body text-sm" data-testid={`company-plan-${c.id}`}>
                  {planById(c.plan_id)?.name || <span className="text-stone-400">—</span>}
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => setFeaturesFor(c)}
                    className="flex flex-wrap gap-1 max-w-[260px] text-left hover:opacity-80"
                    title="Gerenciar recursos ativos"
                    data-testid={`company-features-${c.id}`}
                  >
                    {FEATURE_KEYS.filter((f) => resolveFeatures(c, planById(c.plan_id))[f.key]).map((f) => (
                      <Badge key={f.key} variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">
                        {f.label}
                      </Badge>
                    ))}
                    <Badge variant="outline" className="text-[10px] bg-stone-50 text-stone-600 border-stone-200">
                      <SlidersHorizontal className="h-3 w-3 mr-1" /> editar
                    </Badge>
                  </button>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_COLORS[c.status] || "bg-stone-100"}>
                    {STATUS_LABEL[c.status] || c.status}
                  </Badge>
                  {c.billing_warning && (
                    <Badge className="ml-1 bg-amber-500 text-white border-none text-[10px]" data-testid={`company-warning-${c.id}`}>
                      Aviso de atraso
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-body text-sm">{formatDate(c.created_at)}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`company-actions-${c.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem
                        onClick={() => impersonate(c)}
                        data-testid={`company-impersonate-${c.id}`}
                      >
                        <LogIn className="h-4 w-4 mr-2" /> Acessar painel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setFeaturesFor(c)} data-testid={`company-manage-features-${c.id}`}>
                        <SlidersHorizontal className="h-4 w-4 mr-2" /> Plano e recursos
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {/* FASE 20 — payment-late warning */}
                      {c.billing_warning ? (
                        <DropdownMenuItem onClick={() => act(c.id, "clear-late-notice")} data-testid={`company-clear-late-${c.id}`}>
                          <BellOff className="h-4 w-4 mr-2" /> Remover aviso de atraso
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => act(c.id, "late-notice")} data-testid={`company-late-notice-${c.id}`}>
                          <AlertTriangle className="h-4 w-4 mr-2" /> Aviso de atraso
                        </DropdownMenuItem>
                      )}
                      {c.status !== "suspended" && (
                        <DropdownMenuItem onClick={() => act(c.id, "suspend")} data-testid={`${TID.companySuspend}-${c.id}`}>
                          <PauseCircle className="h-4 w-4 mr-2" /> Suspender
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => act(c.id, "reactivate")} data-testid={`${TID.companyReactivate}-${c.id}`}>
                        <PlayCircle className="h-4 w-4 mr-2" /> Reativar
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => act(c.id, "delete")} className="text-rose-600" data-testid={`${TID.companyCancel}-${c.id}`}>
                        <XCircle className="h-4 w-4 mr-2" /> Cancelar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <CompanyFeaturesDialog company={featuresFor} plans={plans} onOpenChange={() => setFeaturesFor(null)} onSaved={load} />
    </Layout>
  );
}
